import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { useNavigate } from 'react-router-dom';
import { storesAPI, subscriptionsAPI, extractErrorMessage } from '../api';
import { useToast } from '../components/Toast';

const TABS = ['store', 'modules', 'subscription'];

const Settings = () => {
  const { selectedStore, selectStore } = useStore();
  const navigate = useNavigate();
  const showToast = useToast();
  const [activeTab, setActiveTab] = useState('store');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [storeForm, setStoreForm] = useState({
    name: '', brand_name: '', address: '', phone: '', email: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [modules, setModules] = useState([]);
  const [subscription, setSubscription] = useState(null);

  const loadStoreInfo = useCallback(() => {
    if (selectedStore) {
      setStoreForm({
        name: selectedStore.name || '',
        brand_name: selectedStore.brand_name || '',
        address: selectedStore.address || '',
        phone: selectedStore.phone || '',
        email: selectedStore.email || '',
      });
      setModules(selectedStore.modules || []);
    }
  }, [selectedStore]);

  const loadSubscription = useCallback(async () => {
    try {
      const subRes = await subscriptionsAPI.getStoreSubscription(selectedStore.id);
      setSubscription(subRes.data);
    } catch (err) { /* optional */ }
  }, [selectedStore]);

  useEffect(() => {
    loadStoreInfo();
    if (selectedStore) loadSubscription();
  }, [selectedStore, loadStoreInfo, loadSubscription]);

  const handleStoreUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const response = await storesAPI.update(selectedStore.id, storeForm);
      selectStore(response.data);
      showToast('Store updated successfully', 'success');
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to update store'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    try {
      setLoading(true);
      const response = await storesAPI.uploadLogo(selectedStore.id, logoFile);
      selectStore(response.data);
      setLogoFile(null);
      showToast('Logo updated', 'success');
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to upload logo'));
    } finally {
      setLoading(false);
    }
  };

  const handleModuleToggle = async (moduleName, currentEnabled) => {
    try {
      const updatedModules = modules.map(m =>
        m.module_name === moduleName ? { ...m, enabled: !currentEnabled } : m
      );
      await storesAPI.updateModules(selectedStore.id, updatedModules);
      const storeRes = await storesAPI.get(selectedStore.id);
      selectStore(storeRes.data);
      setModules(storeRes.data.modules || updatedModules);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to update modules'));
    }
  };


  return (
    <div className="page-modern">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Configure your store preferences and subscription</p>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Tab Navigation */}
      <div className="tab-bar" style={{ marginBottom: '1.5rem' }}>
        {TABS.map(tab => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'store' ? 'Store Info' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Store Info Tab */}
      {activeTab === 'store' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Store Information</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleStoreUpdate}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Store Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={storeForm.name}
                    onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Brand Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={storeForm.brand_name}
                    onChange={(e) => setStoreForm({ ...storeForm, brand_name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={storeForm.phone}
                    onChange={(e) => setStoreForm({ ...storeForm, phone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={storeForm.email}
                    onChange={(e) => setStoreForm({ ...storeForm, email: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Address</label>
                <textarea
                  className="form-textarea"
                  value={storeForm.address}
                  rows={2}
                  onChange={(e) => setStoreForm({ ...storeForm, address: e.target.value })}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>

            <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border)' }}>
              <label className="form-label">Store Logo</label>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '0.5rem' }}>
                <input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files[0])} />
                {logoFile && (
                  <button className="btn btn-sm btn-primary" onClick={handleLogoUpload} disabled={loading}>
                    Upload Logo
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modules Tab */}
      {activeTab === 'modules' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
          {modules.map(mod => (
            <div className="card" key={mod.module_name}>
              <div className="card-body">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div className="font-bold" style={{ fontSize: '0.9375rem', marginBottom: '0.25rem' }}>
                      {mod.module_name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.8125rem' }}>
                      {mod.enabled ? 'Enabled' : 'Disabled'}
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={mod.enabled}
                      onChange={() => handleModuleToggle(mod.module_name, mod.enabled)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          ))}
          {modules.length === 0 && (
            <div className="card">
              <div className="card-body">
                <div className="page-empty-state" style={{ minHeight: '200px', padding: '1rem' }}>
                  <div className="page-empty-title" style={{ fontSize: '1rem' }}>No modules configured</div>
                  <p className="page-empty-text">Contact support to enable modules for your store</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Subscription Tab */}
      {activeTab === 'subscription' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Subscription</h3>
          </div>
          <div className="card-body">
            {subscription ? (() => {
              const sub = subscription.subscription || subscription;
              return (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Current Plan</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', textTransform: 'capitalize' }}>
                    {(sub.tier || sub.plan || 'Free').replace('_', ' ')}
                  </div>
                  {sub.billing_cycle_end && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                      Renews {new Date(sub.billing_cycle_end).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button className="btn btn-primary" onClick={() => navigate('/billing')}>
                    Manage Billing
                  </button>
                  <button className="btn btn-outline" onClick={() => navigate('/pricing')}>
                    Compare Plans
                  </button>
                </div>
              </div>
              );
            })() : (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>No subscription information available</p>
                <button className="btn btn-primary" onClick={() => navigate('/billing')}>
                  Go to Billing
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
